import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusPill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MaintenancePhotos } from "@/components/MaintenancePhotos";
import { Wrench, Clock, AlertCircle, Camera } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "open", label: "Open", icon: AlertCircle, color: "text-rose-600" },
  { key: "in_progress", label: "In Progress", icon: Clock, color: "text-blue-600" },
  { key: "completed", label: "Completed", icon: Wrench, color: "text-emerald-600" },
];
const PRIORITY = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-600" };

export default function Maintenance() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/maintenance").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      let resolution_note;
      if (status === "completed") resolution_note = "Resolved by property manager.";
      await api.put(`/maintenance/${id}`, { status, resolution_note });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!items) return <Loading />;

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div>
      <PageHeader title="Maintenance" subtitle="Requests submitted by tenants and tracked to completion."
        action={
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="open" data-testid="filter-open">Open</TabsTrigger>
              <TabsTrigger value="completed" data-testid="filter-completed">Done</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      {filtered.length === 0 ? (
        <EmptyState icon={Wrench} title="No maintenance requests" subtitle="Requests your tenants submit will appear here." testid="maint-empty" />
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {COLS.map((col) => {
            const colItems = items.filter((i) => i.status === col.key && (filter === "all" || filter === col.key));
            const Icon = col.icon;
            return (
              <div key={col.key} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`maint-col-${col.key}`}>
                <div className="flex items-center gap-2 mb-4 px-1">
                  <Icon className={`h-4 w-4 ${col.color}`} />
                  <h3 className="font-display font-bold text-slate-900">{col.label}</h3>
                  <span className="ml-auto text-xs font-semibold text-slate-400">{colItems.length}</span>
                </div>
                <div className="space-y-3">
                  {colItems.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Nothing here</p>}
                  {colItems.map((m) => (
                    <div key={m.id} className="rounded-xl border border-slate-200 p-3.5" data-testid={`maint-card-${m.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-slate-900">{m.title}</h4>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${PRIORITY[m.priority]}`}>{m.priority}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1.5 line-clamp-3">{m.description}</p>
                      <div className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">{m.property_name} · {m.tenant_name} · {m.category}
                        {m.photos?.length > 0 && <span className="inline-flex items-center gap-0.5 text-slate-500" data-testid={`maint-photo-count-${m.id}`}><Camera className="h-3 w-3" />{m.photos.length}</span>}
                      </div>
                      <MaintenancePhotos item={m} role="landlord" onChange={load} />
                      {m.resolution_note && m.status === "completed" && (
                        <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-2 py-1.5">{m.resolution_note}</div>
                      )}
                      {m.status !== "completed" && (
                        <div className="mt-3">
                          <Select value={m.status} onValueChange={(v) => setStatus(m.id, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`maint-status-${m.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
